// 止まった長い戻りに体の動きを足す比較版 (DEV ?recoverySway=1・2026-10-25)。
//   記録 = docs/research/2026-10-25-recovery-sway.md / 計画 = docs/research/2026-10-19-boss-polish-vs-popular-games.md §6 段4。
// 戻り (recovery) の間、同じ絵のまま 0.6〜1.1 秒止まって見える技がある (介錯の砲の直射・据えの突き・振り下ろし、騎士の十字、獣の吠えなど)。
//   絵は増やさず、描く時の手続きの動きだけを足す = 打った後に重心が沈んで戻る・武器を引き戻す後ろへの傾き・息。
//   沈みは縦の縮み、引き戻しは足元を支点にした傾きで出す。どちらも足元 (床に置く点) は動かない = 足が床へめり込まず、滑らない。
//   形は戻りの頭と終わりで量も速さも 0 (前後の絵・待機へ跳ばない)。戻りが短い技 (300ms 以下) は 0 で、長いほど満量へ近づく。
//   ゲームの状態 (boss.x・当たり・戻りの長さ・弾ける時間) には触らない。描くだけ。指定なし (配布版) は何もしない。
//   騎士は沈みを入れない (脱力に見える姿勢を挟まない・lanceSweep_release の件)。花嫁は描き直し中なので外す。
//   名も無き亡霊は 2026-10-26 に共通の描く段 (sprites.ts drawShadeSprite) へ載せて対象にした (docs/research/2026-10-26-shade-common-draw.md)。

let on = false
/** DEV の URL の値を受け取る (game.ts の DEV ブロックが ?recoverySway=1 で呼ぶ)。 */
export function setRecoverySway(v: boolean): void { on = v }
export function recoverySwayOn(): boolean { return on }

/** 足す量。rot = 前へ倒れるのが正の度 (支点は足元)・scaleY = 縦の伸び (足元が支点)。 */
export type RecoverySway = { rot: number; scaleY: number }
export const NO_RECOVERY_SWAY: RecoverySway = { rot: 0, scaleY: 1 }

type SwayShape = {
  sink: number        // 打った後に重心が沈む量 = 縦の縮みの割合 (戻りの 3 割で一番深い)
  pullDeg: number     // 武器を引き戻す後ろへの傾き (戻りの 55% で一番)
  breath: number      // 息で縦に伸び縮みする割合
  breathMs: number    // 息 1 回の長さの目安 (戻りの長さをこれで割って回数を決める・最低 1 回)
}

// ボスごとの形。値は 1.3 倍の画面で「見て分かるが、別の動きには見えない」大きさ (検収ページで確かめる)。
//   沈みは介錯 = 背丈 約 175px の 2.2% (約 4px)・獣と覚醒王 = 約 200px の 1.1% (約 2px)。
export const RECOVERY_SWAY_SHAPES: Readonly<Record<string, SwayShape>> = {
  // 大剣・大砲の重さ = 沈んで戻る・大きく引き戻す。
  'the-executioner': { sink: 0.022, pullDeg: 2, breath: 0.012, breathMs: 900 },
  // 動かない圧の騎士 = 沈まない。槍を立て直す小さな傾きと浅い息だけ。
  'vigil-knight': { sink: 0, pullDeg: 1, breath: 0.008, breathMs: 900 },
  // 獣 = 体を引いて、速い息 (あえぐ)。
  'dream-hound': { sink: 0.011, pullDeg: 1.5, breath: 0.016, breathMs: 450 },
  'crowned-king-awoken': { sink: 0.011, pullDeg: 1.5, breath: 0.016, breathMs: 450 },
  // 名も無き亡霊 = 浮いている体。戻りは浮遊の揺れが止まるので、ゆっくり沈んで漂い直す・浅く引く (2026-10-26・背丈 230px の霧の裾込み)。
  'nameless-shade': { sink: 0.012, pullDeg: 1.2, breath: 0.01, breathMs: 900 },
}

/** 戻りの長さで満量へ近づける (300ms 以下は 0・1000ms 以上は満量)。 */
export function recoverySwayStrength(recoveryMs: number): number {
  return Math.min(1, Math.max(0, (recoveryMs - 300) / 700))
}

/** 0 → 1 → 0 の山。p で一番高く、両端と山の上で傾きが 0。 */
export function swayBump(u: number, p: number): number {
  if (u <= 0 || u >= 1) return 0
  if (u < p) return Math.sin((Math.PI / 2) * (u / p)) ** 2
  return Math.cos((Math.PI / 2) * ((u - p) / (1 - p))) ** 2
}

/** 戻りに入ってから elapsedMs の時に足す量。対象外のボス・短い戻り・指定なしは NO_RECOVERY_SWAY。 */
export function recoverySwayMotion(bossId: string, elapsedMs: number, recoveryMs: number): RecoverySway {
  if (!on) return NO_RECOVERY_SWAY
  const shape = RECOVERY_SWAY_SHAPES[bossId]
  const k = recoverySwayStrength(recoveryMs)
  if (!shape || k <= 0 || !(elapsedMs > 0 && elapsedMs < recoveryMs)) return NO_RECOVERY_SWAY
  const u = elapsedMs / recoveryMs
  const settle = swayBump(u, 0.3)
  const pull = swayBump(u, 0.55)
  const cycles = Math.max(1, Math.round(recoveryMs / shape.breathMs))
  return {
    rot: -k * shape.pullDeg * pull,
    scaleY: (1 - k * shape.sink * settle) * (1 + k * shape.breath * Math.sin(2 * Math.PI * cycles * u) * Math.sin(Math.PI * u)),
  }
}
